import {
  ScrollScene,
  ScrollSceneChildProps,
  UseCanvas,
  styles as _styles,
  useScrollRig,
} from "@14islands/r3f-scroll-rig";
//@ts-ignore
import { WebGLText as PowerWebGLText } from "@14islands/r3f-scroll-rig/powerups";
import {
  MeshDistortMaterial,
  MeshTransmissionMaterial,
} from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
//@ts-ignore
import { clamp } from "three/src/math/MathUtils";

import turnipBoldrUrl from "../fonts/Turnip/Turnip-Bold-Edu.woff?url";
import turnipBoldItalicUrl from "../fonts/Turnip/Turnip-BoldItalic-Edu.woff?url";
import turnipItalicUrl from "../fonts/Turnip/Turnip-Italic-Edu.woff?url";
import turnipRegularUrl from "../fonts/Turnip/Turnip-Regular-Edu.woff?url";
import { WebGLText } from "./PowerWebGLText";

const styles = ["Regular", "Bold", "BoldItalic", "Italic"] as const;
type FontStyles = {
  [K in (typeof styles)[number]]: string;
};

// ------------
// Turnip
const turnipMap = _.fromPairs(
  _.zip(styles, [
    turnipRegularUrl,
    turnipBoldrUrl,
    turnipBoldItalicUrl,
    turnipItalicUrl,
  ])
) as FontStyles;

type Tag = "span" | "div" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

interface TextProps {
  wobble?: boolean;
  className?: string;
  font?: string;
  Content?: any; // Should be of type Tag but the props are different...
}

const DIST_SPEED = 1.4;
const DIST_MGN = 0.14;

const Text = ({
  children,
  wobble,
  className,
  font = turnipMap["Regular"],
  Content = "span",
}: React.PropsWithChildren<TextProps>) => {
  const el = useRef<HTMLElement>() as React.MutableRefObject<HTMLElement>;
  const { hasSmoothScrollbar } = useScrollRig();

  return (
    <>
      {/* 
          This is the real DOM text that we want to replace with WebGL
          `styles.transparentColorWhenSmooth` sets the text to transparent when SmoothScrollbar is enabled
          The benefit of using transparent color is that the real DOM text is still selectable
          
          display: 'block' gives a more solid calculation for spans
        */}
      <Content
        ref={el}
        className={_styles.transparentColorWhenSmooth + " " + className}
        style={{ display: "block" }}
      >
        {children}
      </Content>

      {hasSmoothScrollbar && (
        <UseCanvas debug={false}>
          <ScrollScene track={el}>
            {props => (
              // WebGLText is a helper component from the scroll-rig that will
              // use getComputedStyle to match font size, letter spacing and color
              <WebGLText el={el} font={font} {...props}>
                {wobble && (
                  <MeshDistortMaterial
                    distort={DIST_MGN}
                    speed={DIST_SPEED}
                    {...props}
                  />
                )}
                {children}
              </WebGLText>
            )}
          </ScrollScene>
        </UseCanvas>
      )}
    </>
  );
};

export const Headline = ({
  children,
  ...props
}: React.PropsWithChildren<any>) => (
  <Text font={turnipMap["Bold"]} {...props}>
    {children}
  </Text>
);

export const Subtitle = ({
  children,
  ...props
}: React.PropsWithChildren<any>) => (
  <Text font={turnipMap["Italic"]} {...props}>
    {children}
  </Text>
);

export const BodyCopy = Text;

export const BodyText = ({
  children,
  ...props
}: React.PropsWithChildren<any>) => (
  <Text font={turnipMap["Regular"]} {...props}>
    {children}
  </Text>
);
